// Deterministic 2D noise primitives for terrain generation.
// All functions are pure, allocation-free in the inner loop, and guaranteed deterministic.

const HASH_PRIME = new Uint32Array([0x27d4eb2d, 0x165667b1, 0x9e3779b1, 0x85ebca6b]);

export function hash2i(ix, iz, seed) {
  let h = (Math.imul(ix, HASH_PRIME[0]) ^ Math.imul(iz, HASH_PRIME[1]) ^ Math.imul(seed, HASH_PRIME[2])) >>> 0;
  h = Math.imul(h ^ (h >>> 15), HASH_PRIME[3]) >>> 0;
  h = Math.imul(h ^ (h >>> 13), HASH_PRIME[3]) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}

export function smoothstepFade(t) {
  return t * t * (3 - 2 * t);
}

export function valueNoise2(x, z, seed) {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smoothstepFade(x - ix);
  const fz = smoothstepFade(z - iz);

  const n00 = hash2i(ix, iz, seed);
  const n10 = hash2i(ix + 1, iz, seed);
  const n01 = hash2i(ix, iz + 1, seed);
  const n11 = hash2i(ix + 1, iz + 1, seed);

  const top = n00 + fx * (n10 - n00);
  const btm = n01 + fx * (n11 - n01);
  return top + fz * (btm - top);
}

// Fractional Brownian Motion (fBm): layers octaves of value noise
export function fbm(x, z, octaves = 5, seed = 17, lacunarity = 2.03, gain = 0.5) {
  let sum = 0, amp = 1, freq = 1, norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += amp * valueNoise2(x * freq, z * freq, seed + i * 1013);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

// Ridged multifractal: sharp mountain ridges (inverts valleys into peaks)
export function ridged(x, z, octaves = 5, seed = 41, lacunarity = 2.07, gain = 0.52) {
  let sum = 0, amp = 1, freq = 1, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const raw = valueNoise2(x * freq, z * freq, seed + i * 733);
    const n = 1 - Math.abs(raw * 2 - 1);
    sum += amp * (n * n);
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}

// Billowy erosion noise: rounded billowing domes and erosion gullies
export function billow(x, z, octaves = 4, seed = 67, lacunarity = 2.11, gain = 0.48) {
  let sum = 0, amp = 1, freq = 1, norm = 0;
  for (let i = 0; i < octaves; i++) {
    const raw = valueNoise2(x * freq, z * freq, seed + i * 271);
    const n = Math.abs(raw * 2 - 1);
    sum += amp * n;
    norm += amp;
    amp *= gain;
    freq *= lacunarity;
  }
  return sum / norm;
}
